import {clsx} from 'clsx'
import {
  calculateDiffLineCodeCellGutter,
  cellIdFrom,
  getDiffLineMarkersKey,
  getLineBackgroundColor,
  isEmptyDiffLine,
  rowIdFrom,
  trimDiffLineHtml,
} from '../../helpers/line-helpers'
import type {DiffLine, FileDiffReference, LineKey} from '../../types'
import {useMarkersAtPosition, useSelectedCommentId} from '@github-ui/pull-requests/pull-request-store-selectors'
import {InlineMarkers} from '../conversations/InlineMarkers'
import {noop} from '@github-ui/noop'
import {StartConversation} from '../conversations/StartConversation'
import React, {memo, useCallback, useId, useMemo, useRef, useState} from 'react'
import {SimpleActionBar} from '../SimpleActionBar'
import {
  useDialogModeIsEnabled,
  useDiffLineSelectionInfo,
  useDiffAnchor,
  useStoredCommentAtPosition,
  useDialogModeActions,
  useIsDiffLineActionBarVisible,
  useShowDiffLineActionBar,
  useHideDiffLineActionBar,
  useSetFocusedDiffLineActionBar,
  useClearFocusedDiffLineActionBar,
} from '../../stores/selectors'
import {CommentsPreference} from '@github-ui/diff-view-settings/types'
import type {ViewerData} from '../../conversation-types'
import ExpandableHunkHeaderDiffLine from '../ExpandableHunkHeaderDiffLine'
import {getLineHunkData, type HunkData} from '../../helpers/hunk-data-helpers'
import type {DiffLineType} from '@github-ui/diffs/types'
import {LineTextWrapper} from './LineTextWrapper'
import styles from './SimpleDiff.module.css'
import CommentIndicator from '../grid-cells/CommentIndicator'
import {NoEntryIcon} from '@primer/octicons-react'
import {DiffCodeAlert} from '../DiffCodeAlert'
import {HiddenUnicodeAlert} from '@github-ui/hidden-unicode-banner/HiddenUnicodeAlert'
import {navigateToCommentsSplitDiff} from './navigate-to-comments-with-enter-key'
import {lineAnchorFrom, lineOrientationFrom} from '@github-ui/diffs/diff-line-helpers'

const DIMMED_LINE_NUMBER_TYPES: DiffLineType[] = ['CONTEXT', 'INJECTED_CONTEXT']
const EMPTY_CELL_CLASS_NAMES = 'focusable-grid-cell empty-diff-line left-side'
const LINE_NUMBER_CELL_CLASS_NAMES = 'focusable-grid-cell new-diff-line-number left-side'
const CONTENT_CELL_CLASS_NAMES = 'diff-text-cell v-align-top focusable-grid-cell'

export const SimpleSplitDiffRow = memo(function SimpleSplitDiffRow({
  leftDiffLine,
  rightDiffLine,
  nextDiffLine,
  prevDiffLine,
  filePath,
  handleMouseDownOnNumberCell,
  handleMouseEnterCell,
  handleMouseUpRow,
  handleDiffSideCellSelectionBlocking,
  handleMouseLeaveAddCommentButton,
  handleMouseDownAddCommentButton,
  copilotChatReference,
  avatarURL,
  userLogin,
  viewerCanApplySuggestion,
  hunksData,
  viewerCanComment,
  shouldDisplayComments,
}: {
  leftDiffLine: DiffLine | undefined
  rightDiffLine: DiffLine | undefined
  nextDiffLine: DiffLine | undefined
  prevDiffLine: DiffLine | undefined
  hunksData: HunkData[]
  filePath: string
  handleMouseDownOnNumberCell: (e: React.MouseEvent) => void
  handleMouseEnterCell: (e: React.MouseEvent) => void
  handleMouseUpRow: () => {shouldStartConversation: boolean}
  handleDiffSideCellSelectionBlocking: (e: React.MouseEvent) => void
  handleMouseLeaveAddCommentButton: (e: React.MouseEvent) => void
  handleMouseDownAddCommentButton: (e: React.MouseEvent) => void
  copilotChatReference?: FileDiffReference
  avatarURL?: string
  userLogin?: string
  viewerCanApplySuggestion?: boolean
  viewerCanComment?: boolean
  shouldDisplayComments?: boolean
}) {
  const isHunkRow = leftDiffLine?.type === 'HUNK'
  const diffAnchor = useDiffAnchor()
  const leftDiffLineSelectionInfo = useDiffLineSelectionInfo(leftDiffLine?.key)
  const rightDiffLineSelectionInfo = useDiffLineSelectionInfo(rightDiffLine?.key)
  const rowId = rowIdFrom(diffAnchor, leftDiffLine ?? 'empty-diff-line', rightDiffLine)
  const leftLineNumberCellId = cellIdFrom(rowId, 0)
  const leftCodeCellId = cellIdFrom(rowId, 1)
  const rightLineNumberCellId = cellIdFrom(rowId, 2)
  const rightCodeCellId = cellIdFrom(rowId, 3)
  const {enableDialogMode} = useDialogModeActions()
  const isSelected = (leftDiffLineSelectionInfo.isSelected || rightDiffLineSelectionInfo.isSelected) ?? false

  // TODO: line selection should be different color in future iteration
  // as part of https://github.com/github/pull-requests/issues/21372
  const leftLineNumberBackgroundColor = leftDiffLine ? getLineBackgroundColor(leftDiffLine.type, true, isSelected) : ''
  const rightLineNumberBackgroundColor = rightDiffLine
    ? getLineBackgroundColor(rightDiffLine.type, true, isSelected)
    : ''
  const leftCodeBackgroundColor = leftDiffLine ? getLineBackgroundColor(leftDiffLine.type, false, isSelected) : ''
  const rightCodeBackgroundColor = rightDiffLine ? getLineBackgroundColor(rightDiffLine.type, false, isSelected) : ''
  const inlineDialogHeadingId = `inline-dialog-heading-${useId()}`
  const isLeftInDialogMode = useDialogModeIsEnabled(leftCodeCellId)
  const isRightInDialogMode = useDialogModeIsEnabled(rightCodeCellId)

  const [leftLineHtml, leftLineTypeCharacter] = leftDiffLine
    ? trimDiffLineHtml(leftDiffLine.html, leftDiffLine.type)
    : [undefined, undefined]
  const showLeftLineTypeCharacter = leftLineTypeCharacter && ['+', '-'].includes(leftLineTypeCharacter)

  const [rightLineHtml, rightLineTypeCharacter] = rightDiffLine
    ? trimDiffLineHtml(rightDiffLine.html, rightDiffLine.type)
    : [undefined, undefined]
  const showRightLineTypeCharacter = rightLineTypeCharacter && ['+', '-'].includes(rightLineTypeCharacter)

  const leftLineMarkersKey = leftDiffLine ? getDiffLineMarkersKey(leftDiffLine.left, 'LEFT') : undefined
  const {threads: leftThreadsUnfiltered, annotations: leftAnnotations} = useMarkersAtPosition(
    filePath,
    leftLineMarkersKey ?? ('' as LineKey),
  )
  // we do the filtering in the component itself so that the zustand store has a stable reference for the
  // markers at a given position
  const leftThreads = leftThreadsUnfiltered.filter(thread => !thread.shouldNotRenderInDiffLines)

  const hasLeftStoredComment = useStoredCommentAtPosition(leftLineMarkersKey ?? ('' as LineKey))
  const viewerData = {
    avatarUrl: avatarURL ?? '',
    login: userLogin ?? '',
    viewerCanApplySuggestion: viewerCanApplySuggestion ?? false,
    viewerCanComment: viewerCanComment ?? false,
  }
  const rightLineMarkersKey = rightDiffLine ? getDiffLineMarkersKey(rightDiffLine.right, 'RIGHT') : undefined
  const hasRightStoredComment = useStoredCommentAtPosition(rightLineMarkersKey ?? ('' as LineKey))
  const {
    threads: rightThreadsUnfiltered,
    annotations: rightAnnotations,
    commentsPreference,
  } = useMarkersAtPosition(filePath, rightLineMarkersKey ?? ('' as LineKey))
  // we do the filtering in the component itself so that the zustand store has a stable reference for the
  // markers at a given position
  const rightThreads = rightThreadsUnfiltered.filter(thread => !thread.shouldNotRenderInDiffLines)
  const commentsAreMinimized = commentsPreference === CommentsPreference.Collapsed
  const [leftUserToggledOverride, setLeftUserToggledOverride] = useState<boolean>(false)
  const [rightUserToggledOverride, setRightUserToggledOverride] = useState<boolean>(false)

  const selectedCommentId = useSelectedCommentId()

  const hasMatchingLeftComment = useMemo(() => {
    if (selectedCommentId == null || !commentsAreMinimized) return false
    return leftThreads.some(thread =>
      thread.commentsData?.comments.some(comment => comment.databaseId === selectedCommentId),
    )
  }, [selectedCommentId, leftThreads, commentsAreMinimized])

  const hasMatchingRightComment = useMemo(() => {
    if (selectedCommentId == null || !commentsAreMinimized) return false
    return rightThreads.some(thread =>
      thread.commentsData?.comments.some(comment => comment.databaseId === selectedCommentId),
    )
  }, [selectedCommentId, rightThreads, commentsAreMinimized])

  const leftCommentMinimizeOverride = hasMatchingLeftComment || leftUserToggledOverride
  const rightCommentMinimizeOverride = hasMatchingRightComment || rightUserToggledOverride

  const [isLeftNewConversationDialogOpen, setIsLeftNewConversationDialogOpen] = useState(hasLeftStoredComment ?? false)
  const [isLeftContextMenuOpen, setIsLeftContextMenuOpen] = useState(false)
  const [shouldLeftStartNewConversationWithSuggestedChange, setShouldLeftStartNewConversationWithSuggestedChange] =
    useState(false)
  const [isRightNewConversationDialogOpen, setIsRightNewConversationDialogOpen] = useState(
    hasRightStoredComment ?? false,
  )
  const [isRightContextMenuOpen, setIsRightContextMenuOpen] = useState(false)
  const [shouldRightStartNewConversationWithSuggestedChange, setShouldRightStartNewConversationWithSuggestedChange] =
    useState(false)
  const [showLeftInlineMarkers, setShowLeftInlineMarkers] = useState(
    leftThreads.length > 0 || leftAnnotations.length > 0 || isLeftNewConversationDialogOpen,
  )
  const [showRightInlineMarkers, setShowRightInlineMarkers] = useState(
    rightThreads.length > 0 || rightAnnotations.length > 0 || isRightNewConversationDialogOpen,
  )
  const hasLeftThreads = leftThreads.length + leftAnnotations.length !== 0
  const hasRightThreads = rightThreads.length + rightAnnotations.length !== 0
  const leftCommentIndicatorGutterSize = calculateDiffLineCodeCellGutter({
    hasThreads: hasLeftThreads && commentsAreMinimized,
  })
  const rightCommentIndicatorGutterSize = calculateDiffLineCodeCellGutter({
    hasThreads: hasRightThreads && commentsAreMinimized,
  })
  const currentDiffLine = !isEmptyDiffLine(leftDiffLine)
    ? leftDiffLine
    : !isEmptyDiffLine(rightDiffLine)
      ? rightDiffLine
      : undefined
  const shouldDisplayLeftInlineComments =
    shouldDisplayComments &&
    (!commentsAreMinimized || leftCommentMinimizeOverride || isLeftNewConversationDialogOpen) &&
    (showLeftInlineMarkers || hasLeftThreads || hasLeftStoredComment || isLeftNewConversationDialogOpen)
  const shouldDisplayRightInlineComments =
    shouldDisplayComments &&
    (!commentsAreMinimized || rightCommentMinimizeOverride || isRightNewConversationDialogOpen) &&
    (showRightInlineMarkers || hasRightThreads || hasRightStoredComment || isRightNewConversationDialogOpen)
  // TODO: This needs to be reworked in future iteration
  // as part of https://github.com/github/pull-requests/issues/21372
  const inlineMarkersRef = useRef<HTMLDivElement | null>(null)
  const leftCellRef = useRef<HTMLTableCellElement | null>(null)
  const rightCellRef = useRef<HTMLTableCellElement | null>(null)

  const handleOnKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      navigateToCommentsSplitDiff(e, {
        hasLeftThreads,
        hasRightThreads,
        hasRightStoredComment,
        hasLeftStoredComment,
        enableDialogMode,
        leftCodeCellId,
        rightCodeCellId,
        setIsLeftNewConversationDialogOpen,
        setIsRightNewConversationDialogOpen,
      })
    },
    [
      enableDialogMode,
      hasLeftThreads,
      hasRightThreads,
      hasRightStoredComment,
      hasLeftStoredComment,
      leftCodeCellId,
      rightCodeCellId,
    ],
  )

  const showDiffLineActionBar = useShowDiffLineActionBar()
  const hideDiffLineActionBar = useHideDiffLineActionBar()
  const setFocusedActionBar = useSetFocusedDiffLineActionBar()
  const clearFocusedActionBar = useClearFocusedDiffLineActionBar()
  const isLeftActionBarVisible = useIsDiffLineActionBarVisible(leftCodeCellId)
  const isRightActionBarVisible = useIsDiffLineActionBarVisible(rightCodeCellId)

  // Handle mouseUp on row - if drag started from add comment button, open conversation dialog
  const handleRowMouseUp = useCallback(
    (e: React.MouseEvent) => {
      const {shouldStartConversation} = handleMouseUpRow()

      if (shouldStartConversation) {
        // Determine which side of the diff the mouse was released on
        const el = e.target as HTMLElement
        const diffSide = el.closest('[data-diff-side]')?.getAttribute('data-diff-side')

        if (diffSide === 'left' && leftDiffLine) {
          setIsLeftNewConversationDialogOpen(true)
          setShowLeftInlineMarkers(true)
        } else if (diffSide === 'right' && rightDiffLine) {
          setIsRightNewConversationDialogOpen(true)
          setShowRightInlineMarkers(true)
        } else if (leftDiffLine) {
          // Fallback to left side if no side detected but left line exists
          setIsLeftNewConversationDialogOpen(true)
          setShowLeftInlineMarkers(true)
        } else if (rightDiffLine) {
          // Fallback to right side
          setIsRightNewConversationDialogOpen(true)
          setShowRightInlineMarkers(true)
        }
      }
    },
    [
      handleMouseUpRow,
      leftDiffLine,
      rightDiffLine,
      setIsLeftNewConversationDialogOpen,
      setShowLeftInlineMarkers,
      setIsRightNewConversationDialogOpen,
      setShowRightInlineMarkers,
    ],
  )

  if (!currentDiffLine) return null
  const {currentHunk, nextHunk, previousHunk} = getLineHunkData(currentDiffLine, hunksData)
  const leftLineAnchor = lineAnchorFrom(
    diffAnchor,
    lineOrientationFrom(leftDiffLine?.type ?? 'EMPTY'),
    leftDiffLine?.blobLineNumber ?? 0,
  )
  const rightLineAnchor = lineAnchorFrom(
    diffAnchor,
    lineOrientationFrom(rightDiffLine?.type ?? 'EMPTY'),
    rightDiffLine?.blobLineNumber ?? 0,
  )

  return (
    <tr className="diff-line-row" onMouseUp={handleRowMouseUp}>
      {isHunkRow ? (
        <ExpandableHunkHeaderDiffLine
          currentHunk={currentHunk}
          diffAnchor={diffAnchor}
          diffLine={leftDiffLine}
          isSplit
          nextHunk={nextHunk}
          nextLine={nextDiffLine as DiffLine}
          previousHunk={previousHunk}
          prevLine={prevDiffLine as DiffLine}
        />
      ) : (
        <>
          {!leftDiffLine ? (
            <>
              <td role="gridcell" className={EMPTY_CELL_CLASS_NAMES} tabIndex={-1} />
              <td role="gridcell" className={EMPTY_CELL_CLASS_NAMES} tabIndex={-1} />
            </>
          ) : (
            <>
              <td
                className={clsx(
                  LINE_NUMBER_CELL_CLASS_NAMES,
                  DIMMED_LINE_NUMBER_TYPES.includes(leftDiffLine.type) && 'diff-line-number-neutral',
                )}
                data-diff-side="left"
                data-diff-line-key={leftDiffLine.key}
                data-grid-cell-id={leftLineNumberCellId}
                data-line-number={leftDiffLine.left}
                data-selected={isSelected}
                onMouseDown={handleMouseDownOnNumberCell}
                onMouseEnter={handleMouseEnterCell}
                onKeyDown={handleOnKeyDown}
                role="gridcell"
                style={{backgroundColor: leftLineNumberBackgroundColor}}
                tabIndex={-1}
              >
                {leftDiffLine.hasHiddenUnicode && (
                  <DiffCodeAlert>
                    <HiddenUnicodeAlert />
                  </DiffCodeAlert>
                )}
                {leftDiffLine.left}
              </td>
              <td
                className={clsx(CONTENT_CELL_CLASS_NAMES, 'left-side-diff-cell')}
                data-grid-cell-id={leftCodeCellId}
                data-diff-line-key={leftDiffLine.key}
                data-diff-side="left"
                data-line-number={leftDiffLine.left}
                data-line-anchor={leftLineAnchor}
                data-selected={isSelected}
                onKeyDown={handleOnKeyDown}
                onMouseDown={handleDiffSideCellSelectionBlocking}
                onMouseEnter={(e: React.MouseEvent) => {
                  showDiffLineActionBar(leftCodeCellId)
                  handleMouseEnterCell(e)
                }}
                onMouseLeave={() => hideDiffLineActionBar(leftCodeCellId)}
                onFocus={() => setFocusedActionBar(leftCodeCellId)}
                onBlur={event => {
                  const nextFocus = event.relatedTarget
                  if (nextFocus instanceof Node && event.currentTarget.contains(nextFocus)) {
                    return
                  }
                  clearFocusedActionBar()
                }}
                aria-labelledby={isLeftInDialogMode ? inlineDialogHeadingId : undefined}
                role={isLeftInDialogMode ? 'dialog' : 'gridcell'}
                ref={node => {
                  if (!isLeftActionBarVisible) {
                    leftCellRef.current = null
                  } else {
                    leftCellRef.current = node
                  }
                }}
                style={{backgroundColor: leftCodeBackgroundColor, paddingRight: leftCommentIndicatorGutterSize}}
                tabIndex={-1}
              >
                {isLeftInDialogMode && (
                  <h1 id={inlineDialogHeadingId} className="sr-only">
                    Comment view
                  </h1>
                )}
                <LineTextWrapper
                  lineHtml={leftLineHtml ?? ''}
                  lineType={leftDiffLine.type}
                  lineTypeCharacter={showLeftLineTypeCharacter ? leftLineTypeCharacter : undefined}
                />
                {leftDiffLine.displayNoNewLineWarning && <NoEntryIcon size={16} className="fgColor-danger" />}
                {shouldDisplayLeftInlineComments && (
                  <InlineMarkers
                    diffLine={leftDiffLine}
                    gridCellId={leftCodeCellId}
                    shouldListenForEscapeKey
                    gutterSizeOffset={leftCommentIndicatorGutterSize}
                    inlineMarkersRef={inlineMarkersRef}
                    cellRef={leftCellRef}
                    fileAnchor={diffAnchor}
                    batchingEnabled
                    // passing a noop because in simplified view we are not dealing with collapsed comments
                    dispatchMarkersStatus={noop}
                    lineMarkersKey={leftLineMarkersKey as LineKey}
                    annotations={leftAnnotations}
                    conversationListThreads={leftThreads}
                    filePath={filePath}
                    onCloseFocusMode={noop}
                    isRowSelected={isSelected}
                    lineType={leftDiffLine.type}
                    onCloseConversationList={() => {
                      setIsLeftNewConversationDialogOpen(false)
                      setShowLeftInlineMarkers(false)
                      setShouldLeftStartNewConversationWithSuggestedChange(false)
                    }}
                    onDeleteLineComment={() => {
                      setIsLeftNewConversationDialogOpen(false)
                      setShowLeftInlineMarkers(false)
                      setShouldLeftStartNewConversationWithSuggestedChange(false)
                    }}
                    viewerData={viewerData as ViewerData}
                  >
                    {(isLeftNewConversationDialogOpen || hasLeftStoredComment) && (
                      <div className={clsx(' border rounded-2 color-border-default', 'mt-2 mb-1')}>
                        <StartConversation
                          diffLine={leftDiffLine}
                          diffAnchor={diffAnchor}
                          batchingEnabled
                          filePath={filePath}
                          focusOnMount={!hasLeftStoredComment}
                          isLeftSide
                          isInDialogMode={false}
                          isOpen
                          isRowSelected={isSelected}
                          viewerData={viewerData}
                          shouldStartNewConversationWithSuggestedChange={
                            shouldLeftStartNewConversationWithSuggestedChange
                          }
                          onCloseCommentDialog={() => {
                            leftCellRef.current?.focus()
                            setIsLeftNewConversationDialogOpen(false)
                            setShowLeftInlineMarkers(false)
                            setShouldLeftStartNewConversationWithSuggestedChange(false)
                          }}
                        />
                      </div>
                    )}
                  </InlineMarkers>
                )}
                {(isLeftActionBarVisible || isLeftContextMenuOpen) && (
                  <SimpleActionBar
                    openStartConversationDialog={(value: boolean) => {
                      setShouldLeftStartNewConversationWithSuggestedChange(value)
                      setIsLeftNewConversationDialogOpen(true)
                      setShowLeftInlineMarkers(true)
                    }}
                    isAddCommentBoxOpen={isLeftNewConversationDialogOpen}
                    authorAvatarUrl={avatarURL ?? ''}
                    authorLogin={userLogin ?? ''}
                    ref={leftCellRef}
                    copilotChatReference={copilotChatReference}
                    currentHunk={currentHunk}
                    diffAnchor={diffAnchor}
                    diffLine={leftDiffLine}
                    threads={leftThreads}
                    annotations={leftAnnotations}
                    hasDraftComment={hasLeftStoredComment}
                    handleMouseLeaveAddCommentButton={handleMouseLeaveAddCommentButton}
                    handleMouseDownAddCommentButton={handleMouseDownAddCommentButton}
                    isLeftSide
                    isSplit
                    contextMenuOpen={isLeftContextMenuOpen}
                    setIsContextMenuOpen={setIsLeftContextMenuOpen}
                    cellId={leftCodeCellId}
                    nextHunk={nextHunk}
                    previousHunk={previousHunk}
                    shouldDisplayComments={shouldDisplayComments}
                    shouldDisplayMinimizedComments={shouldDisplayComments}
                    shouldDisplayCollapseComments={leftCommentMinimizeOverride}
                    filePath={filePath}
                    toggleViewingMarkers={() => setLeftUserToggledOverride(!leftUserToggledOverride)}
                    viewerData={{
                      avatarUrl: avatarURL ?? '',
                      diffViewPreference: 'split',
                      lineSpacingPreference: 'compact',
                      login: userLogin ?? '',
                      commentsPreference,
                      shouldDisplayComments: shouldDisplayComments ?? true,
                      shouldDisplayEditOptions: true,
                      tabSizePreference: 8,
                      viewerCanComment: viewerCanComment ?? false,
                      viewerCanApplySuggestion: viewerCanApplySuggestion ?? false,
                    }}
                  />
                )}
                {(leftThreads.length > 0 || leftAnnotations.length > 0) && commentsAreMinimized && (
                  <div
                    aria-hidden="true"
                    className={clsx('position-absolute top-0 d-flex user-select-none', styles['comment-indicator'])}
                  >
                    <CommentIndicator
                      threads={leftThreads}
                      annotations={leftAnnotations}
                      lineSpacingPreference={'compact'}
                    />
                  </div>
                )}
              </td>
            </>
          )}

          {!rightDiffLine ? (
            <>
              <td role="gridcell" className={EMPTY_CELL_CLASS_NAMES} tabIndex={-1} />
              <td role="gridcell" className={EMPTY_CELL_CLASS_NAMES} tabIndex={-1} />
            </>
          ) : (
            <>
              <td
                className={clsx(
                  LINE_NUMBER_CELL_CLASS_NAMES,
                  DIMMED_LINE_NUMBER_TYPES.includes(rightDiffLine.type) && 'diff-line-number-neutral',
                )}
                data-diff-side="right"
                data-diff-line-key={rightDiffLine.key}
                data-grid-cell-id={rightLineNumberCellId}
                data-line-number={rightDiffLine.right}
                data-selected={isSelected}
                onKeyDown={handleOnKeyDown}
                onMouseDown={handleMouseDownOnNumberCell}
                onMouseEnter={handleMouseEnterCell}
                role="gridcell"
                style={{backgroundColor: rightLineNumberBackgroundColor}}
                tabIndex={-1}
              >
                {rightDiffLine.hasHiddenUnicode && (
                  <DiffCodeAlert>
                    <HiddenUnicodeAlert />
                  </DiffCodeAlert>
                )}
                {rightDiffLine.right}
              </td>
              <td
                className={clsx(CONTENT_CELL_CLASS_NAMES, 'right-side-diff-cell')}
                data-grid-cell-id={rightCodeCellId}
                data-diff-line-key={rightDiffLine.key}
                data-diff-side="right"
                data-line-anchor={rightLineAnchor}
                data-line-number={rightDiffLine.right}
                data-selected={isSelected}
                onKeyDown={handleOnKeyDown}
                onMouseDown={handleDiffSideCellSelectionBlocking}
                onMouseEnter={(e: React.MouseEvent) => {
                  showDiffLineActionBar(rightCodeCellId)
                  handleMouseEnterCell(e)
                }}
                onMouseLeave={() => hideDiffLineActionBar(rightCodeCellId)}
                onFocus={() => setFocusedActionBar(rightCodeCellId)}
                onBlur={event => {
                  const nextFocus = event.relatedTarget
                  if (nextFocus instanceof Node && event.currentTarget.contains(nextFocus)) {
                    return
                  }
                  clearFocusedActionBar()
                }}
                aria-labelledby={isRightInDialogMode ? inlineDialogHeadingId : undefined}
                role={isRightInDialogMode ? 'dialog' : 'gridcell'}
                ref={node => {
                  if (!isRightActionBarVisible) {
                    rightCellRef.current = null
                  } else {
                    rightCellRef.current = node
                  }
                }}
                style={{backgroundColor: rightCodeBackgroundColor, paddingRight: rightCommentIndicatorGutterSize}}
                tabIndex={-1}
              >
                {isRightInDialogMode && (
                  <h1 id={inlineDialogHeadingId} className="sr-only">
                    Comment view
                  </h1>
                )}
                <LineTextWrapper
                  lineHtml={rightLineHtml ?? ''}
                  lineType={rightDiffLine.type}
                  lineTypeCharacter={showRightLineTypeCharacter ? rightLineTypeCharacter : undefined}
                />
                {rightDiffLine.displayNoNewLineWarning && <NoEntryIcon size={16} className="fgColor-danger" />}
                {shouldDisplayRightInlineComments && (
                  <InlineMarkers
                    diffLine={rightDiffLine}
                    gridCellId={rightCodeCellId}
                    shouldListenForEscapeKey
                    gutterSizeOffset={rightCommentIndicatorGutterSize}
                    inlineMarkersRef={inlineMarkersRef}
                    cellRef={rightCellRef}
                    fileAnchor={diffAnchor}
                    batchingEnabled
                    // passing a noop because in simplified view we are not dealing with collapsed comments
                    dispatchMarkersStatus={noop}
                    lineMarkersKey={rightLineMarkersKey as LineKey}
                    annotations={rightAnnotations}
                    conversationListThreads={rightThreads}
                    filePath={filePath}
                    viewerData={viewerData as ViewerData}
                    onCloseFocusMode={noop}
                    isRowSelected={isSelected}
                    lineType={rightDiffLine.type}
                    onCloseConversationList={() => {
                      setIsRightNewConversationDialogOpen(false)
                      setShowRightInlineMarkers(false)
                      setShouldRightStartNewConversationWithSuggestedChange(false)
                    }}
                    onDeleteLineComment={() => {
                      setIsRightNewConversationDialogOpen(false)
                      setShowRightInlineMarkers(false)
                      setShouldRightStartNewConversationWithSuggestedChange(false)
                    }}
                  >
                    {(isRightNewConversationDialogOpen || hasRightStoredComment) && (
                      <div className={clsx(' border rounded-2 color-border-default', 'mt-2 mb-1')}>
                        <StartConversation
                          diffLine={rightDiffLine}
                          diffAnchor={diffAnchor}
                          batchingEnabled
                          filePath={filePath}
                          focusOnMount={!hasRightStoredComment}
                          isLeftSide={false}
                          isInDialogMode={false}
                          isOpen
                          isRowSelected={isSelected}
                          shouldStartNewConversationWithSuggestedChange={
                            shouldRightStartNewConversationWithSuggestedChange
                          }
                          viewerData={viewerData}
                          onCloseCommentDialog={() => {
                            rightCellRef.current?.focus()
                            setIsRightNewConversationDialogOpen(false)
                            setShowRightInlineMarkers(false)
                            setShouldRightStartNewConversationWithSuggestedChange(false)
                          }}
                        />
                      </div>
                    )}
                  </InlineMarkers>
                )}
                {(isRightActionBarVisible || isRightContextMenuOpen) && (
                  <SimpleActionBar
                    openStartConversationDialog={(value: boolean) => {
                      setShouldRightStartNewConversationWithSuggestedChange(value)
                      setIsRightNewConversationDialogOpen(true)
                      setShowRightInlineMarkers(true)
                    }}
                    authorAvatarUrl={avatarURL ?? ''}
                    authorLogin={userLogin ?? ''}
                    ref={rightCellRef}
                    isAddCommentBoxOpen={isRightNewConversationDialogOpen}
                    copilotChatReference={copilotChatReference}
                    currentHunk={currentHunk}
                    diffAnchor={diffAnchor}
                    diffLine={rightDiffLine}
                    threads={rightThreads}
                    contextMenuOpen={isRightContextMenuOpen}
                    setIsContextMenuOpen={setIsRightContextMenuOpen}
                    cellId={rightCodeCellId}
                    annotations={rightAnnotations}
                    hasDraftComment={hasRightStoredComment}
                    handleMouseLeaveAddCommentButton={handleMouseLeaveAddCommentButton}
                    handleMouseDownAddCommentButton={handleMouseDownAddCommentButton}
                    isLeftSide={false}
                    isSplit
                    nextHunk={nextHunk}
                    previousHunk={previousHunk}
                    filePath={filePath}
                    shouldDisplayComments={shouldDisplayComments}
                    shouldDisplayMinimizedComments={shouldDisplayComments}
                    shouldDisplayCollapseComments={rightCommentMinimizeOverride}
                    toggleViewingMarkers={() => setRightUserToggledOverride(!rightUserToggledOverride)}
                    viewerData={{
                      avatarUrl: avatarURL ?? '',
                      diffViewPreference: 'split',
                      lineSpacingPreference: 'compact',
                      login: userLogin ?? '',
                      commentsPreference,
                      shouldDisplayComments: shouldDisplayComments ?? true,
                      shouldDisplayEditOptions: true,
                      tabSizePreference: 8,
                      viewerCanComment: viewerCanComment ?? false,
                      viewerCanApplySuggestion: viewerCanApplySuggestion ?? false,
                    }}
                  />
                )}
                {(rightThreads.length > 0 || rightAnnotations.length > 0) && commentsAreMinimized && (
                  <div
                    aria-hidden="true"
                    className={clsx('position-absolute top-0 d-flex user-select-none', styles['comment-indicator'])}
                  >
                    <CommentIndicator
                      threads={rightThreads}
                      annotations={rightAnnotations}
                      lineSpacingPreference={'compact'}
                    />
                  </div>
                )}
              </td>
            </>
          )}
        </>
      )}
    </tr>
  )
})
